import { Controller } from "@hotwired/stimulus"

// Quick Take runtime — drives one interactive toast on the applicant's page.
//
// The component (`components/survey/_quick_take_toast` in interactive mode)
// renders all four screens present-but-hidden with real controls; this
// controller steps through them, resolves the by-rating branching client-side
// from the chosen rating, and posts the completion / dismissal.
//
// Values (set on the toast shell by the component):
//   content     : Object — the full `content` JSON
//   metric      : Object — the survey's scale and bucket bounds, as
//                 { type, min, max, buckets: { low: [from, to], … } }
//   oneTap      : Boolean — nothing follows the rating and the scale is one
//                 unambiguous choice, so the tap itself submits and the
//                 component draws no confirm button
//   deliveryId  : Number — the Survey::QuickTakeDelivery id
//   completeUrl : String — PATCH endpoint to record a response on full completion
//   dismissUrl  : String — PATCH endpoint to dismiss without a response
//
// A response is captured ONLY on full completion (reaching thank-you / the final
// submit). Closing (×) at any point before that dismisses the toast and saves
// nothing. By-rating buckets are NOT redefined here: the bounds arrive in
// `metricValue` from Survey::QuickTakeMetric, so the split has one definition
// shared by the builder, the server-rendered toast and this controller. Leaf
// text is written via `textContent`, never innerHTML, so server-stored copy
// stays XSS-safe.

const THANK_YOU_LINGER_MS = 3000

export default class extends Controller {
  static targets = [
    "startScreen", "primaryMetric", "additionalQuestion", "thankYou",
    "stars", "aqInput", "metricButton", "aqButton"
  ]
  static values = {
    content: Object,
    metric: Object,
    oneTap: Boolean,
    deliveryId: Number,
    completeUrl: String,
    dismissUrl: String
  }

  connect() {
    this.rating = null
    this.completed = false
    // A screen toggled "hidden" in the builder is skipped at runtime: a hidden
    // start screen drops off the front, so the toast opens on the rating.
    this.show(this.startScreenHidden() ? "primaryMetric" : "startScreen")
  }

  next() {
    if (this.currentScreen === "startScreen") {
      this.show("primaryMetric")
    } else if (this.currentScreen === "primaryMetric") {
      // Skip the follow-up when the bucket the chosen rating resolves to has
      // nothing to ask (removed, or a by-rating rating with no question) — go
      // straight to completion.
      if (this.additionalQuestionSkipped()) {
        this.submit()
      } else {
        this.enterAdditionalQuestion()
      }
    }
  }

  rate(event) {
    this.rating = Number(event.currentTarget.dataset.rating)
    this.paintScale(event.currentTarget)
    if (this.hasMetricButtonTarget) this.metricButtonTarget.disabled = false

    // A one-tap toast has no confirm button to enable — the tap is the answer.
    // Close the row as well as submitting, so a second tap has nothing to hit
    // while the first is still in flight.
    if (this.oneTapValue) {
      this.sealScale()
      this.submit()
    }
  }

  // Takes the scale out of play once the answer is in. Used by the one-tap
  // flow, where there is no confirm button standing between the tap and the
  // submission.
  sealScale() {
    if (!this.hasStarsTarget) return

    this.starsTarget.querySelectorAll("[data-rating]").forEach((control) => {
      control.disabled = true
    })
  }

  // Submit the answer and record the response. On success either close (skip
  // thank-you) or show the by-rating thank-you message and linger before leaving.
  async submit() {
    // `completed` is only true once the POST has come back, so it cannot guard
    // the window while one is still in flight. A one-tap scale has no confirm
    // button to disable, so two quick taps would otherwise post twice and record
    // two responses for one delivery.
    if (this.completed || this.submitting) return
    // A required follow-up must be answered before it can be submitted (the
    // button is disabled in this state, but guard the action too).
    if (this.requiredAnswerMissing()) return

    this.submitting = true
    const answer = this.hasAqInputTarget ? this.aqInputTarget.value : ""
    const ok = await this.post(this.completeUrlValue, { rating: this.rating, answer })
    // Keep the toast on a network/validation error — no destructive UI — and let
    // the respondent try again.
    this.submitting = false
    if (!ok) return

    this.completed = true

    if (this.thankYouSkipped()) {
      this.leave()
      return
    }

    this.fillThankYou()
    this.show("thankYou")
    this.lingerTimer = setTimeout(() => this.leave(), THANK_YOU_LINGER_MS)
  }

  // The × at any point: a completed toast just leaves; an in-progress one is
  // dismissed (no response) first.
  close() {
    if (this.lingerTimer) clearTimeout(this.lingerTimer)

    if (!this.completed) this.post(this.dismissUrlValue, {})
    this.leave()
  }

  // --- screen flow ----------------------------------------------------------

  show(screen) {
    this.currentScreen = screen
    this.screens().forEach(([name, section]) => {
      if (section) section.hidden = name !== screen
    })
  }

  enterAdditionalQuestion() {
    this.fillAdditionalQuestion()
    this.show("additionalQuestion")
    this.refreshAqButton()
  }

  // A required follow-up keeps its submit button disabled until a non-blank
  // answer is typed; an optional one never blocks. Re-run on enter and on input.
  refreshAqButton() {
    if (this.hasAqButtonTarget) this.aqButtonTarget.disabled = this.requiredAnswerMissing()
  }

  requiredAnswerMissing() {
    if (this.currentScreen !== "additionalQuestion") return false

    const required = Boolean(this.resolveBucket(this.additionalQuestionSection()).required)
    const answered = this.hasAqInputTarget && this.aqInputTarget.value.trim().length > 0
    return required && !answered
  }

  // Fills the follow-up question/description from the bucket the chosen rating
  // resolves to (or the shared copy), mirroring the server helper.
  fillAdditionalQuestion() {
    const bucket = this.resolveBucket(this.additionalQuestionSection())
    this.setLeaf(this.additionalQuestionTarget, ".quick-take-toast__title", bucket.question)
    this.setLeaf(this.additionalQuestionTarget, ".quick-take-toast__body", bucket.description)
  }

  fillThankYou() {
    const bucket = this.resolveBucket(this.thankYouSection())
    this.setLeaf(this.thankYouTarget, ".quick-take-toast__message", bucket.message)
  }

  // The shared copy, or the bucket the chosen rating falls into. The bounds come
  // from the metric, so a scale with no middle bucket (or a 0–10 one) needs no
  // change here.
  resolveBucket(section) {
    if (!section || section.mode !== "by_rating") return (section && section.shared) || {}

    const key = this.bucketKey()
    return (key && section.by_rating && section.by_rating[key]) || {}
  }

  bucketKey() {
    const buckets = this.metricValue.buckets || {}
    return Object.keys(buckets).find((key) => {
      const [from, to] = buckets[key]
      return this.rating >= from && this.rating <= to
    })
  }

  // Marks the chosen control. The row declares whether its scale is cumulative
  // (a star rating: the pick and everything before it) or single-select (a face:
  // only the pick). CSS paints each state; this only sets `is-selected`.
  paintScale(chosen) {
    if (!this.hasStarsTarget) return

    const cumulative = this.starsTarget.dataset.cumulative === "true"
    this.starsTarget.querySelectorAll("[data-rating]").forEach((control) => {
      const selected = cumulative ? Number(control.dataset.rating) <= this.rating : control === chosen
      control.classList.toggle("is-selected", selected)
    })
  }

  // --- leaving / reflow -----------------------------------------------------

  // Fades and slides the toast out; once the transition ends the node is removed
  // and the flex stack reflows so the remaining toasts descend into the gap.
  leave() {
    this.element.classList.add("quick-take-toast--leaving")
    this.element.addEventListener("transitionend", () => this.element.remove(), { once: true })
  }

  // --- helpers --------------------------------------------------------------

  screens() {
    return [
      ["startScreen", this.hasStartScreenTarget ? this.startScreenTarget : null],
      ["primaryMetric", this.hasPrimaryMetricTarget ? this.primaryMetricTarget : null],
      ["additionalQuestion", this.hasAdditionalQuestionTarget ? this.additionalQuestionTarget : null],
      ["thankYou", this.hasThankYouTarget ? this.thankYouTarget : null]
    ]
  }

  setLeaf(scope, selector, value) {
    const node = scope.querySelector(selector)
    if (node && value != null && String(value).trim()) node.textContent = value
  }

  // A builder "hide" toggle skips the screen at runtime. Only the start screen
  // and the follow-up buckets carry a hide toggle (the rating has none); the
  // follow-up's hidden-ness is rating-dependent, so it reads the resolved bucket.
  startScreenHidden() {
    return Boolean(this.section("start_screen").hidden)
  }

  // The follow-up is skipped when the bucket the chosen rating resolves to was
  // removed (hidden), or — in by-rating mode — when that rating has no question
  // configured (e.g. only 1–2★ and 4–5★ are set and the rating is 3★).
  additionalQuestionSkipped() {
    const section = this.additionalQuestionSection()
    const bucket = this.resolveBucket(section)
    if (bucket.hidden) return true
    if (section.mode !== "by_rating") return false

    return this.blank(bucket.question)
  }

  // The thank-you is skipped — the toast just records and leaves — when the
  // builder turned it off (mode "skip"), or, in by-rating mode, when the chosen
  // rating resolves to a bucket with no message (the same "no bucket for this
  // rating" case as the follow-up above).
  thankYouSkipped() {
    const section = this.thankYouSection()
    if (section.mode === "skip") return true
    if (section.mode !== "by_rating") return false

    return this.blank(this.resolveBucket(section).message)
  }

  blank(value) {
    return !String(value == null ? "" : value).trim()
  }

  additionalQuestionSection() {
    return this.section("additional_question")
  }

  thankYouSection() {
    return this.section("thank_you")
  }

  section(key) {
    const section = (this.contentValue || {})[key]
    return section && typeof section === "object" ? section : {}
  }

  // PATCHes the endpoint with the CSRF token; resolves true only on an ok
  // response so the caller can keep the toast on a network/validation error.
  async post(url, body) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify(body)
      })
      return response.ok
    } catch (error) {
      return false
    }
  }
}
